import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import { Patient, PatientQuery } from '@dreryk/egabinet-patient-frontend-api';
import { TaxRate, TaxRateQuery } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { paginations } from '@dreryk/command-query-ws-client';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { invoiceSharedTranslationKeys } from '../../translations';

type InvoiceCreateFormLoaderRenderProps = {
   patient: Patient | undefined;
   taxRates: TaxRate[] | undefined;
   visitSettlement: VisitSettlement | undefined;
};

type InvoiceCreateFormLoaderProps = {
   readonly patientId: number;
   readonly appointmentId: number;
   readonly children: (renderProps: InvoiceCreateFormLoaderRenderProps) => JSX.Element;
};

export const InvoiceCreateFormLoader = (props: InvoiceCreateFormLoaderProps): JSX.Element => {
   const { data: patient, error: patientError, loading: patientLoading } = useCqrsGet<Patient>(() => {
      const query = new PatientQuery();
      query.id = Slot.of(props.patientId);
      return query;
   });

   const {
      data: visitSettlement,
      error: visitSettlementError,
      loading: visitSettlementLoading,
   } = useCqrsGet<VisitSettlement>(() => {
      const query = new VisitSettlementQuery();
      query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(props.appointmentId))];
      return query;
   });

   const { data: taxRates, loading: taxRatesLoading, error: taxRatesError } = useCqrsSearch<TaxRate>(
      () => {
         const query = new TaxRateQuery();
         return query;
      },
      paginations.all(),
      {
         cacheTime: 'long',
      }
   );

   if (patientLoading || visitSettlementLoading || taxRatesLoading) {
      return <LoaderSpinner />;
   }

   if (patientError || visitSettlementError || taxRatesError) {
      return (
         <>
            <Message
               testId={'failedToFetchData'}
               label={invoiceSharedTranslationKeys.fetchError}
               type={MessageType.NEGATIVE}
               displayIndicator
            />
            {props.children({
               patient: undefined,
               taxRates: undefined,
               visitSettlement: undefined,
            })}
         </>
      );
   }

   return props.children({
      patient: patient,
      taxRates: taxRates,
      visitSettlement: visitSettlement,
   });
};
